import React from "react"
import ReactDOM from "react-dom/client"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import RouteErrorFallback from "@/app/components/RouteErrorFallback"
import { routeTree } from "./routeTree.gen"
import * as Sentry from "@sentry/react"
import { setupNativeBridgeDevMock } from "@/utils/nativeBridge"
import "./app/global.scss"
import { useAuth } from "@/providers/AuthProvider.tsx"
import { QueryProvider, AuthProvider } from "./providers"

setupNativeBridgeDevMock()

Sentry.init({
  dsn: "https://d612bb4d80e2b9555c5d4f5691434254@o4507230158454784.ingest.us.sentry.io/4510673926029312",
  environment: import.meta.env.VITE_MODE || "production",
  enabled: !import.meta.env.DEV,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration(), Sentry.browserProfilingIntegration()],
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/([a-zA-Z0-9-]+\.)+ohcoach\.com$/,
    /^https:\/\/([a-zA-Z0-9-]+\.)+fitogether\.com$/,
  ],
})

// Create a new router instance
const router = createRouter({
  routeTree,
  // 라우트 어디선가 렌더 예외가 나면 기본 "Something went wrong!" 흰 화면 대신 우리 안내 화면 (2026-09-28)
  defaultErrorComponent: RouteErrorFallback,
  context: {
    authenticated: false,
    token: undefined,
    login: undefined,
    logout: undefined,
  },
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

export function App() {
  const authContext = useAuth()

  return <RouterProvider router={router} context={authContext} />
}

// Render the app
const rootElement = document.getElementById("root")!
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <QueryProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </QueryProvider>,
  )
}
